import Bugsnag from '@bugsnag/js';
import S3 from '@uppy/aws-s3';
import Uppy, { UppyFile } from '@uppy/core';
import { v4 as uuid } from 'uuid';
import { getCsrfToken } from '@99designs/browser';

const Megabyte = 1024 * 1024;

export type UppyFileWithKey = UppyFile<{ key: string }>;

export interface UploaderConfig {
    requestAccessKey: string;
    requestEndpoint: string;
    signatureEndpoint: string;
    bucket: string;
    keyPrefix?: string;
    successEndpoint?: string;
    region?: string;
    signatureVersion?: number;
    allowedFileTypes?: string[];
}

const defaultAllowedFileTypes = [
    '.ai',
    '.bmp',
    '.cdr',
    '.cgm',
    '.clk',
    '.cur',
    '.doc',
    '.docx',
    '.emf',
    '.epi',
    '.eps',
    '.eps2',
    '.eps3',
    '.epsf',
    '.epsi',
    '.ept',
    '.exif',
    '.fla',
    '.gif',
    '.gfa',
    '.giff',
    '.ico',
    '.ind',
    '.indd',
    '.jpe',
    '.jpeg',
    '.jpg',
    '.odg',
    '.pct',
    '.pcx',
    '.pdf',
    '.pic',
    '.pict',
    '.png',
    '.pot',
    '.potx',
    '.pps',
    '.ppt',
    '.pptx',
    '.psd',
    '.ps',
    '.pub',
    '.raw',
    '.rtf',
    '.svg',
    '.svgz',
    '.swf',
    '.tif',
    '.tiff',
    '.txt',
    '.wmf',
    '.zip',
];

export function createUploader({ keyPrefix, allowedFileTypes }: UploaderConfig): Uppy.Uppy {
    const trimmedKeyPrefix = keyPrefix?.endsWith('/') ? keyPrefix.slice(0, -1) : keyPrefix ?? '';
    const urlParts = trimmedKeyPrefix.split('/');
    const briefId = urlParts.length > 0 ? urlParts[urlParts.length - 1] : '';
    const uppy = Uppy({
        autoProceed: true,
        restrictions: {
            maxFileSize: 100 * Megabyte,
            maxNumberOfFiles: null,
            minNumberOfFiles: null,
            allowedFileTypes: allowedFileTypes || defaultAllowedFileTypes,
        },
        locale: {
            strings: {
                exceedsSize: 'exceedsSize',
            },
        },
    });

    uppy.on('error', (error: Error) => {
        Bugsnag.notify(error);
    })
        .on('upload-error', (file: UppyFileWithKey, error: Error) => {
            Bugsnag.notify(error, (event) => {
                event.addMetadata('uploader', 'file', file); // TODO: test no sensitive data
            });
        })
        .on('file-added', (file: UppyFileWithKey) => {
            uppy.setFileMeta(file.id, {
                key: `${keyPrefix || ''}${uuid()}.${file.extension}`,
            });
        })
        .use(S3, {
            getUploadParameters: async (file: UppyFileWithKey) => {
                const response = await fetch(`/create/upload/${briefId}`, {
                    method: 'POST',
                    headers: {
                        accept: 'application/json',
                        'X-CSRF-Token': getCsrfToken(),
                    },
                    body: JSON.stringify({ key: file.meta.key }),
                });

                if (response.status !== 200) {
                    const err = await response.text();
                    throw new Error('upload signing error: ' + err);
                }

                const { signature } = await response.json();
                return {
                    method: 'PUT',
                    url: signature,
                    fields: {},
                    headers: {},
                };
            },
            timeout: 0,
        });

    return uppy;
}
